import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isMobileAboutDropdownOpen, setIsMobileAboutDropdownOpen] = useState(false);
  const location = useLocation();

  // Custom navigation function that ensures page refresh
  const handleNavigation = (path) => {
    setIsMobileMenuOpen(false);
    setIsMobileAboutDropdownOpen(false);
    setIsAboutDropdownOpen(false);

    // Force page refresh by using window.location for navigation
    if (location.pathname !== path) {
      window.location.href = path;
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes and force refresh
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileAboutDropdownOpen(false);
    // Force scroll to top on route change
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.mobile-menu') && !event.target.closest('.hamburger-button')) {
        setIsMobileMenuOpen(false);
        setIsMobileAboutDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { path: '/', label: 'Home' },
    {
      label: 'About',
      hasDropdown: true,
      dropdownItems: [
        { path: '/about', label: 'About Us' },
        // { path: '/our-team', label: 'Our Team' },
        { path: '/promise-to-parents', label: 'Promise to Parents' },
        { path: '/why-aureus', label: 'Why Aureus?' }
      ]
    },
    { path: '/programmes', label: 'Programmes' },
    { path: '/curriculum', label: 'Curriculum' },
    // { path: '/term-calendar', label: 'Term Calendar' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/faq', label: 'FAQ' },
    { path: '/contact', label: 'Contact' },
    { path: '/apply-online', label: 'Apply Online' }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-transparent">
      {/* Black bar behind pill when scrolled */}
      <div className={`fixed top-0 left-0 right-0 h-20 z-40 transition-colors duration-300 ${isScrolled ? 'bg-black/80' : 'bg-transparent'}`}></div>
      <div className="relative z-50 max-w-6xl px-2 mx-auto my-4 bg-transparent">
        <div className={`flex items-center justify-between border border-yellow-400 rounded-full shadow-md px-6 py-2 transition-colors duration-300 ${isScrolled ? 'bg-black/60' : 'bg-transparent'}`}>
          {/* Logo */}
          <button onClick={() => handleNavigation('/')} className="flex flex-col items-start">
            <span className={`text-3xl font-bold tracking-widest select-none transition-colors duration-300 leading-none ${isScrolled ? 'text-white' : 'text-white'}`} style={{ fontFamily: 'Playfair Display, serif' }}>AUREUS</span>
            <span className={`text-xs font-normal tracking-widest select-none transition-colors duration-300 leading-none -mt-1 ${isScrolled ? 'text-white' : 'text-white'}`} style={{ fontFamily: 'Playfair Display, serif' }}>CAMBRIDGE SCHOOL</span>
          </button>
          {/* Nav Links */}
          <div className="justify-center flex-1 hidden lg:flex">
            <div className="flex gap-2">
              {navLinks.map((link, index) => {
                if (link.hasDropdown) {
                  const isAboutActive = link.dropdownItems.some(item => location.pathname === item.path);
                  return (
                    <div
                      key={`desktop-dropdown-${index}`}
                      className="relative group"
                      onMouseEnter={() => setIsAboutDropdownOpen(true)}
                      onMouseLeave={() => setIsAboutDropdownOpen(false)}
                    >
                      <button
                        className={`px-5 py-2 rounded-full font-medium transition-all duration-200 text-sm flex items-center gap-1 ${isAboutActive
                          ? `bg-yellow-400 ${isScrolled ? 'text-black' : 'text-white'} shadow font-semibold`
                          : 'text-white hover:bg-yellow-100 hover:text-yellow-700'
                          }`}
                      >
                        {link.label}
                        <ChevronDownIcon className="w-4 h-4" />
                      </button>

                      {/* Dropdown Menu */}
                      <div className={`absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 transition-all duration-200 ${isAboutDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'
                        }`}>
                        {link.dropdownItems.map((item, itemIndex) => (
                          <button
                            key={`desktop-dropdown-item-${itemIndex}`}
                            onClick={() => handleNavigation(item.path)}
                            className="block w-full px-4 py-3 text-left no-underline transition-colors duration-200 text-slate-700 hover:bg-yellow-50 hover:text-yellow-700"
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                } else {
                  const isActive = location.pathname === link.path;
                  return (
                    <button
                      key={`desktop-link-${link.path}`}
                      onClick={() => handleNavigation(link.path)}
                      className={`px-5 py-2 rounded-full font-medium transition-all duration-200 text-sm ${isActive
                        ? `bg-yellow-400 ${isScrolled ? 'text-black' : 'text-white'} shadow font-semibold`
                        : 'text-white hover:bg-yellow-100 hover:text-yellow-700'
                        }`}
                    >
                      {link.label}
                    </button>
                  );
                }
              })}
            </div>
          </div>
          {/* Hamburger */}
          <div className="flex items-center lg:hidden">
            <button
              className="p-2 bg-transparent hamburger-button focus:outline-none"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <span className="block w-6 h-0.5 transition-colors duration-300 mb-1" style={{ backgroundColor: '#fff' }}></span>
              <span className="block w-6 h-0.5 transition-colors duration-300 mb-1" style={{ backgroundColor: '#fff' }}></span>
              <span className="block w-6 h-0.5 transition-colors duration-300" style={{ backgroundColor: '#fff' }}></span>
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed left-0 right-0 z-50 py-4 border-t border-yellow-400 shadow-md mobile-menu lg:hidden top-20 bg-black/95 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-2">
            {navLinks.map((link, index) => {
              if (link.hasDropdown) {
                return (
                  <div key={`mobile-dropdown-${index}`} className="w-11/12">
                    <button
                      className="flex items-center justify-center w-full gap-1 py-2 mb-2 text-sm font-medium text-center text-white transition-colors duration-200 hover:text-yellow-400"
                      onClick={() => setIsMobileAboutDropdownOpen(!isMobileAboutDropdownOpen)}
                    >
                      {link.label}
                      <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${isMobileAboutDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Mobile Dropdown Items */}
                    <div className={`transition-all duration-300 overflow-hidden ${isMobileAboutDropdownOpen ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                      {link.dropdownItems.map((item, itemIndex) => {
                        const isActive = location.pathname === item.path;
                        return (
                          <button
                            key={`mobile-dropdown-item-${itemIndex}`}
                            onClick={() => handleNavigation(item.path)}
                            className={`block px-5 py-2 rounded-full font-medium transition-all duration-200 text-sm w-full text-center mb-1 ${isActive
                              ? `bg-yellow-400 text-black shadow font-semibold`
                              : 'text-white hover:bg-yellow-100 hover:text-yellow-700'
                              }`}
                          >
                            {item.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              } else {
                const isActive = location.pathname === link.path;
                return (
                  <button
                    key={`mobile-link-${link.path}`}
                    onClick={() => handleNavigation(link.path)}
                    className={`px-5 py-2 rounded-full font-medium transition-all duration-200 text-sm w-11/12 text-center ${isActive
                      ? `bg-yellow-400 text-black shadow font-semibold`
                      : 'text-white hover:bg-yellow-100 hover:text-yellow-700'
                      }`}
                  >
                    {link.label}
                  </button>
                );
              }
            })}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar; 